<template>
	<div>
		<div class="columns">
			<div class="column">
				<div class="list-title">
					{{ !selectedTab ? $t('statistics.events') : $t('statistics.inspections') }}
				</div>
				<BarChart
					v-if="!selectedTab"
					:key="$i18n.locale" 
					:chartData="getFlawsDone"
					:chartData2="getFlawsUndone"
					:labels="labels"
					:categoryLabels="categoryLabels"
					:colors="colors"
				/>

				<div v-else class="list-container inner column">
					<div class="panel-list-header extra-narrow mobile_statistics">
						<div class="panel-header-item mobile_statistics__header">Namn</div>
						<div class="panel-header-item narrow">{{ $t('statistics.january') }}</div>
							<div class="panel-header-item narrow">{{ $t('statistics.february') }}</div>
							<div class="panel-header-item narrow">{{ $t('statistics.march') }}</div>
							<div class="panel-header-item narrow">{{ $t('statistics.april') }}</div>
							<div class="panel-header-item narrow">{{ $t('statistics.may') }}</div>
							<div class="panel-header-item narrow">{{ $t('statistics.june') }}</div>
							<div class="panel-header-item narrow">{{ $t('statistics.july') }}</div>
							<div class="panel-header-item narrow">{{ $t('statistics.august') }}</div>
							<div class="panel-header-item narrow">{{ $t('statistics.september') }}</div>
							<div class="panel-header-item narrow">{{ $t('statistics.october') }}</div>
							<div class="panel-header-item narrow">{{ $t('statistics.november') }}</div>
							<div class="panel-header-item narrow">{{ $t('statistics.december') }}</div>
					</div>
					<div
						class="panel-list extra-narrow"
						v-for="(round, index) in roundPlanningStatistics"
						:key="index"
					>
						<p class="panel-list-item mobile-data">
							{{ round.name }}
						</p>
						<div
							class="panel-circle mobile-data__list"
							v-for="(history, index) in round.roundHistoryStatistics"
							:key="index"
						>
							<i class="fas fa-circle" :class="getColor(history)"></i>
						</div>
					</div>
				</div>
			</div>
			<div class="column info" :class="{ mobile: isMobile }">
				<div class="list-title">{{ $t('statistics.info') }}</div>
				<div class="info-box">
					<div class="field">
						<label class="label">{{ $t('statistics.year_label') }}</label>
						<div class="control">
							<v-select
								class="panel-select year"
								v-model="year"
								:options="years"
								:placeholder="$t('statistics.year_placeholder')"
								:allowEmpty="false"
								:internalSearch="true"
								:showLabels="false"
								@select="setCurrentYear"
							>
								<span slot="noResult">{{ $t('statistics.no_results') }}</span>
								<span
									slot="caret"
									slot-scope="{ toggle }"
									class="caret"
									@mousedown.prevent.stop="toggle()"
								><i class="fas fa-caret-down caret-inner"></i
								></span>
							</v-select>
						</div>
					</div>
					<div class="field">
						<div class="info-text-box">{{ infoBox }}</div>
					</div>
					<div class="tabs is-toggle is-fullwidth">
						<ul>
							<li
								:class="selectedTab == 0 ? 'is-active' : ''"
								@click="selectTab(0)"
							>
								<a>
									<p>{{ $t('statistics.events') }}</p>
								</a>
							</li>
							<li
								:class="selectedTab == 1 ? 'is-active' : ''"
								@click="selectTab(1)"
							>
								<a>
									<p>{{ $t('statistics.inspections') }}</p>
								</a>
							</li>
						</ul>
					</div>
				</div>
				<PieChart
					:key="$i18n.locale + '-' + selectedTab"
					:chartData="pieData"
					:labels="pieLabels"
					:colors="pieColors"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import BarChart from '@/components/BarChart';
import PieChart from '@/components/PieChart';
import { mapGetters } from 'vuex';

export default {
	components: {
		BarChart,
		PieChart
	},

	data() {
		return {
			fixed: [],
			roundCount: [],
			colors: ['#7bca78', '#da6060'],
			years: [],
			selectedTab: 0,
			year: null,

			flawStatistics: {},
			roundPlanningStatistics: []
		};
	},

	computed: {
		...mapGetters('user', ['getActiveCustomer']),
		...mapGetters('appStore', ['isMobile']),
		...mapGetters('buildingStore', ['selectedBuilding', 'getUserBuildingId']),

		getFlawsUndone() {
			return this.flawStatistics.flawCounts
				? this.flawStatistics.flawCounts.map(count => count.flaws, [])
				: [];
		},

		getFlawsDone() {
			return this.flawStatistics.flawCounts
				? this.flawStatistics.flawCounts.map(count => count.doneFlaws, [])
				: [];
		},

		pieData() {
			if (this.selectedTab === 1) {
				return [
					this.getControllerStatisticsData.completed,
					this.getControllerStatisticsData.todo,
					this.getControllerStatisticsData.ongoing,
					this.getControllerStatisticsData.ongoingExpired
				];
			}

			return [
				this.flawStatistics.totalFlawsDone,
				this.flawStatistics.totalFlawsNotCompleted
			];
		},

		categoryLabels() {
			const _ = this.$i18n.locale;
			return [
			this.$t('statistics.completed'),
			this.$t('statistics.not_completed')
			];
		},

		pieLabels() {
			const _ = this.$i18n.locale;
			return this.selectedTab
			? [
				this.$t('statistics.completed'),
				this.$t('statistics.not_completed'),
				this.$t('statistics.ongoing'),
				this.$t('statistics.started_but_expired'),
				]
			: this.categoryLabels;
		},

		pieColors() {
			return this.selectedTab ? ['#7bca78', '#da6060', '#ffe000', '#ff9500'] : this.colors;
		},

		infoBox() {
			this.getRoundHistoryCount();

			return !this.selectedTab
			? this.$t('statistics.total_flaws', {
				count: this.flawStatistics.totalFlaws,
				fixed: this.flawStatistics.totalFlawsDone
				})
			: `${this.comDoneText}
				${this.comOngoingText}
				${this.comIncompleteText}
				${this.comStartedButExpiredText}`;
		},

		getControllerStatisticsData() {
			return {
				completed: this.roundCount[0],
				ongoing: this.roundCount[1],
				todo: this.roundCount[2],
				ongoingExpired: this.roundCount[3]
			};
		},

		comDoneText() {
			return this.roundCount[0] === 1
				? this.$t('statistics.done_inspection', { count: this.roundCount[0] })
				: this.$t('statistics.done_inspections', { count: this.roundCount[0] });
		},

		comOngoingText() {
			return this.roundCount[1] === 1
				? this.$t('statistics.ongoing_inspection', { count: this.roundCount[1] })
				: this.$t('statistics.ongoing_inspections', { count: this.roundCount[1] });
		},

		comIncompleteText() {
			return this.roundCount[2] === 1
				? this.$t('statistics.incomplete_inspection', { count: this.roundCount[2] })
				: this.$t('statistics.incomplete_inspections', { count: this.roundCount[2] });
		},

		comStartedButExpiredText() {
			return this.roundCount[3] === 1
				? this.$t('statistics.expired_inspection', { count: this.roundCount[3] })
				: this.$t('statistics.expired_inspections', { count: this.roundCount[3] });
		},

		getTriggerValue() {
			return `${this.getActiveCustomer?.id}-${this.selectedBuilding.id}-${this.getUserBuildingId}`;
		},
		labels() {
			const _ = this.$i18n.locale; 
			return [
				this.$t('statistics.january'),
				this.$t('statistics.february'),
				this.$t('statistics.march'),
				this.$t('statistics.april'),
				this.$t('statistics.may'),
				this.$t('statistics.june'),
				this.$t('statistics.july'),
				this.$t('statistics.august'),
				this.$t('statistics.september'),
				this.$t('statistics.october'),
				this.$t('statistics.november'),
				this.$t('statistics.december')
			];
		}
	},

	watch: {
		getTriggerValue() {
			this.refreshData();
		},
		labels(newLabels) {
			if (this.chart) {
			this.chart.data.labels = newLabels;
			this.chart.update();
			}
		},
		chartData(newData) {
			if (this.chart) {
			this.chart.data.datasets[0].data = newData;
			this.chart.update();
			}
		}
	},

	methods: {
		selectTab(index) {
			this.selectedTab = index;
		},

		getColor(roundHistory) {
			const colorMap = {
				0: 'green',
				1: 'red',
				2: 'yellow',
				3: 'grey',
				4: 'orange'
			};
			const colorIndex = roundHistory.started && roundHistory.expired
				? 4
				: roundHistory.done && !roundHistory.expired
					? 0
					: roundHistory.started
						? 2
						: !roundHistory.created || !roundHistory.done
							? 3
							: 1;

			return colorMap[colorIndex];
		},

		setCurrentYear(year) {
			this.year = year;

			this.refreshData();
		},

		async getStatistics(buildingId, year) {
			await this.getFlawStatistic(buildingId, year);
			await this.getRoundStatistics(buildingId, year);
		},

		async getFlawStatistic(buildingId, year) {
			const data = await this.$services.flaw.getFlawStatistic({
				customerId: this.getActiveCustomer?.id,
				userBuildingId: this.getUserBuildingId,
				buildingId: buildingId,
				Start: `${year}-01-01`,
				Stop: `${year}-12-31`,
				pageSize: 0
			});

			this.flawStatistics = data;
		},

		async getRoundStatistics(buildingId, year) {
			const data = await this.$services.roundPlanning.getRoundStatistics({
				customerId: this.getActiveCustomer?.id,
				buildingId: buildingId,
				Start: `${year}-01-01`,
				Stop: `${year}-12-31`,
				pageSize: 0,
				orderBy: 'Name'
			});

			this.roundPlanningStatistics = data;
		},

		getRoundHistoryCount() {
			const currentMonth = this.moment(new Date()).format('M');

			if (this.roundPlanningStatistics.length  < 1) return;

			this.roundCount = this.roundPlanningStatistics.reduce((roundAcc, round) => {
				let roundCount = round.roundHistoryStatistics.reduce((acc, history) => {
					const roundHistoryMonth = history.created ? this.moment(history.created).format('M') : -1;

					if (history.done && !history.expired) acc[0] = acc[0] + 1;

					if (!history.done && history.started) acc[1] = acc[1] + 1;

					if (history.done && currentMonth !== roundHistoryMonth && roundHistoryMonth !== -1) acc[2] = acc[2] + 1;

					if (history.started && history.expired) acc[3] = acc[3] + 1;

					return acc;
				},[ 0, 0, 0, 0 ]);

				roundAcc[0] = roundAcc[0] + roundCount[0];
				roundAcc[1] = roundAcc[1] + roundCount[1];
				roundAcc[2] = roundAcc[2] + roundCount[2];
				roundAcc[3] = roundAcc[3] + roundCount[3];

				return roundAcc;
			}, [ 0, 0, 0, 0 ]);
		},

		refreshData() {
			this.getStatistics(this.selectedBuilding.id, this.year);
		}
	},

	created() {
		this.year = this.moment().format('YYYY');
		const yearsSinceStart = this.moment().diff('2020-01-01', 'years');
		this.years = [];
		for(let i = 0; i <= yearsSinceStart; i++) {
			this.years.push(this.moment('2020-01-01').add(i, 'years').format('YYYY'));
		}
		this.years.reverse();

		this.getStatistics(this.selectedBuilding.id, this.year);
	}
};
</script>

<style lang="scss" scoped>
@import "@/assets/colors.scss";

.column.info {
  max-width: 300px;
  .info-box {
    background-color: $white;
    width: 100%;
    padding: 0.5rem;
  }
  &.mobile {
    max-width: 100%;
  }
}
.info-text-box {
  background-color: white;
  border: 1px solid $multiselect-border-color;
  padding: 4px 8px;
  font-size: 14px;
  white-space: pre-line;
}
a {
  background-color: white;
}

li {
  &.is-active {
    a {
      background-color: $lightest-grey !important;
      border-color: $lighter-grey !important;
    }
    p {
      color: $dark;
    }
  }
}
.panel-header-item.narrow {
  max-width: 46px;
  text-align: center;
}
.panel-circle {
  min-width: 46px;
  text-align: center;
  i {
    &.green {
      color: #7bca78 !important;
    }
    &.yellow {
      color: #ffe000 !important;
    }
    &.red {
      color: #da6060 !important;
    }
    &.grey {
      color: lightgray !important;
    }
    &.orange {
        color: #ff9500;
    }
  }
}
</style>

<style lang="scss">
.multiselect.panel-select.year {
  .caret {
    background-color: #fcfcfc;
  }
  .multiselect__tags {
    background-color: white;

    .multiselect__single,
    .multiselect__input {
      background-color: white;
    }
  }
  .multiselect__content {
    background: white;
  }
}
 @media screen and (max-width: 600px) {
  .mobile_statistics {
    font-size: 9px !important;
    &__header {
      min-width: 46px !important;
    }
  }
  .mobile-data {
    font-size: 9px !important;
    padding-top: 5px !important;
    min-width: 46px !important;
  }

  .mobile-data__list {
    width: 100% !important;
    min-width: 0px !important;
  }
}
</style>
